import styles from './style.module.scss';
import { useEffect, useRef, useState } from 'react';
import { useIsMobile } from 'hooks/use-size-class';

import type { SearchOurProperties as SearchOurPropertiesConfig } from '@tenants';
import DeferredImage from 'components/image/DeferredImage';

const DEFAULT_SCROLL_SPEED = 0.3;
const MOBILE_SCROLL_SPEED = 0.7;

type SearchOurPropertiesProps = SearchOurPropertiesConfig;

const generateRandomWidths = (count: number, isMobile: boolean) => {
  const minWidth = isMobile ? 200 : 300;
  const maxWidth = isMobile ? 300 : 500;
  const minDifference = 50; // Minimum difference between consecutive widths
  const widths: number[] = [];

  const randomInRange = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

  for (let i = 0; i < count; i++) {
    if (widths.length === 0) {
      widths.push(randomInRange(minWidth, maxWidth));
      continue;
    }

    // Pick uniformly from the values that are at least `minDifference` away from
    // the previous width: [minWidth, prev - minDifference] ∪ [prev + minDifference, maxWidth].
    // This guarantees the constraint instead of relying on capped random retries.
    const prev = widths[widths.length - 1];
    const lowCount = Math.max(0, prev - minDifference - minWidth + 1);
    const highCount = Math.max(0, maxWidth - (prev + minDifference) + 1);
    const total = lowCount + highCount;

    if (total <= 0) {
      widths.push(randomInRange(minWidth, maxWidth));
      continue;
    }

    const pick = Math.floor(Math.random() * total);
    const newWidth = pick < lowCount ? minWidth + pick : prev + minDifference + (pick - lowCount);
    widths.push(newWidth);
  }

  return widths;
};

const ensureMinimumImages = (images: readonly string[], minCount: number = 10): string[] => {
  if (images.length >= minCount) {
    return [...images];
  }

  const repeatedImages = [...images];
  while (repeatedImages.length < minCount) {
    repeatedImages.push(...images);
  }

  // Trim to exact minimum count if we went over
  return repeatedImages.slice(0, minCount);
};

export default function SearchOurProperties({ title, linkText, linkPath, imageCarousel }: SearchOurPropertiesProps) {
  const isMobile = useIsMobile();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [isInView, setIsInView] = useState(false);
  const [sectionTop, setSectionTop] = useState(0);

  // Ensure we have at least 10 images
  const extendedImageCarousel = ensureMinimumImages(imageCarousel);
  const [randomWidths, setRandomWidths] = useState<number[]>([]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- widths use Math.random(); generate client-only after mount to avoid SSR/client hydration mismatch, and regenerate when isMobile changes.
    setRandomWidths(generateRandomWidths(extendedImageCarousel.length, isMobile));
  }, [extendedImageCarousel.length, isMobile]);

  // Intersection Observer to detect when section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting && sectionRef.current) {
          // Store the section's top position when it comes into view
          const rect = sectionRef.current.getBoundingClientRect();
          setSectionTop(window.scrollY + rect.top);
        }
      },
      {
        threshold: 0.10, // Trigger when 20% of the section is visible
        rootMargin: '0px 0px -10% 0px', // Start slightly before it's fully in view
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const currentRef = sectionRef.current;
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!isInView) return;

      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
        // Calculate scroll offset from when section came into view
        const scrollOffset = currentScrollTop - sectionTop;
        const scrollSpeed = isMobile ? MOBILE_SCROLL_SPEED : DEFAULT_SCROLL_SPEED;
        // Negative value to go left when scrolling down, positive when scrolling up
        const translateX = -(scrollOffset * scrollSpeed);
        scrollContainer.style.transform = `translateX(${translateX + (isMobile ? 0 : 250)}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInView, sectionTop, isMobile]);

  return (
    <div className={styles.container} ref={sectionRef}>
      {title && <h2 dangerouslySetInnerHTML={{ __html: title.__html }} />}

      {linkText && <a href={linkPath}>{linkText}</a>}

      <div data-testid="carousel" className={styles.imageCarousel} ref={scrollContainerRef}>
        {extendedImageCarousel.map((image, index) => (
          <div
            key={`${image}-${index}`}
            className={styles['img-container']}
            style={{ '--random-width': `${randomWidths[index]}px` } as React.CSSProperties}
          >
            <div>
              <DeferredImage src={image} alt={`Commercial property example ${index + 1}`} fill quality={70} sizes='(max-width: 640px) 70vw, 40vw' />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
